// DatabaseSettingsRollupRow.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './DatabaseSettingsRollupRow.css';

// Icons
import Icon from 'svg/Icon';

/**
 * DatabaseSettingsRollupRow
 *
 * Renders one draggable rollup row in the database settings Rollups tab.
 *
 * @param {boolean} isDragging - Whether the row is currently being dragged.
 * @param {boolean} isDropTarget - Whether the row is the active drop target.
 * @param {string} label - Primary rollup title.
 * @param {string} note - Secondary rollup summary.
 * @param {function} onClick - Opens the rollup editor.
 * @param {function} onDragEnd - Clears drag state.
 * @param {function} onDragOver - Handles drag-over events.
 * @param {function} onDragStart - Starts dragging.
 * @param {function} onDrop - Handles drop.
 * @param {function} onSettingsClick - Opens the rollup editor via the settings action.
 *
 * @returns {ReactNode} The rendered rollup row.
 */
const DatabaseSettingsRollupRow = React.memo(({
  isDragging,
  isDropTarget,
  label,
  note,
  onClick,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDrop,
  onSettingsClick,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- RENDER ----- //

  return (
    <div
      className={`database-settings-rollup-row-container${isDragging ? ' dragging' : ''}`}
      style={{
        backgroundColor: isDropTarget ? theme.backgroundHover : theme.panel,
        borderColor: isDropTarget ? theme.foregroundMuted : theme.backgroundHover,
      }}
      draggable
      title={label || 'Rollup'}
      onClick={onClick}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragStart={onDragStart}
      onDrop={onDrop}
    >

      {/* MAIN */}
      <div className="database-settings-rollup-row-main">

        {/* ICON */}
        <div className="database-settings-rollup-row-icon">
          <Icon
            iconKey="query_stats"
            width={18}
            height={18}
            color={theme.foregroundMuted}
          />
        </div>

        {/* COPY */}
        <div className="database-settings-rollup-row-copy">

          {/* LABEL */}
          <div
            className="database-settings-rollup-row-label"
          >
            {label}
          </div>

          {/* NOTE */}
          {note ? (
            <div
              className="database-settings-rollup-row-note"
            >
              {note}
            </div>
          ) : null}
        </div>
      </div>

      {/* ACTIONS */}
      <div className="database-settings-rollup-row-actions">

        {/* ACTION */}
        <button
          className="database-settings-rollup-row-action"
          title="Edit rollup"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onSettingsClick?.();
          }}
        >
          <Icon
            iconKey="settings"
            width={16}
            height={16}
            color={theme.foregroundMuted}
          />
        </button>

        {/* DRAG HANDLE */}
        <div
          className="database-settings-rollup-row-drag-handle"
          title="Drag to reorder"
        >
          <Icon
            iconKey="drag_indicator"
            width={18}
            height={18}
            color={theme.foregroundMuted}
          />
        </div>
      </div>
    </div>
  );
});

// Dev Tools
DatabaseSettingsRollupRow.displayName = 'DatabaseSettingsRollupRow';

export default DatabaseSettingsRollupRow;
